import type { LoginRequest, LoginResponse } from './auth.api'

/**
 * Access tokens are held in memory only, never in localStorage (spec §5, §12).
 * The provider hides whether tokens come from email/password, OIDC or the local mock.
 */
export interface TokenProvider {
  readonly kind: 'password' | 'oidc' | 'mock'
  /** Try to restore a session silently on app start. Resolves true if a token is available. */
  init(): Promise<boolean>
  getAccessToken(): Promise<string | null>
  /** Refresh the access token. Resolves null if the session cannot be renewed. */
  refresh(): Promise<string | null>
  /**
   * Sign in with credentials. Keeps the token when the response carries one; when it asks for
   * scope selection there is no token yet. Absent for redirect-only providers (OIDC).
   */
  signInWithPassword?(request: LoginRequest): Promise<LoginResponse>
  /** Start redirect sign-in. `redirect` is the in-app path to return to. */
  signIn(redirect?: string, hint?: string): Promise<void>
  /** Complete the redirect flow on /callback. Resolves the in-app path to return to. */
  handleCallback(): Promise<string | undefined>
  signOut(): Promise<void>
}

let current: TokenProvider | null = null

export function setTokenProvider(provider: TokenProvider) {
  current = provider
}

export function tokenProvider(): TokenProvider {
  if (!current) throw new Error('TokenProvider not configured')
  return current
}
